import React, { useEffect, useState, useMemo } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import {
  VStack, HStack, Text, Box, Center, Spinner,
} from 'native-base';
import { Card } from 'react-native-paper';
import { Linking, ScrollView, StyleSheet } from 'react-native';
import { format } from 'date-fns';
import Constants from 'expo-constants';
import { TRIP_BY_SHARE_TOKEN } from '../../gql/trip/query';
import { DRIVER_LOCATION_BY_SHARE_TOKEN } from '../../gql/trip/subscription';
import { calculateDistance } from '../../utils/distance';
import { decodePolyline } from '../../utils/polyline';
import TripMap from '../../components/maps/TripMap';
import TripProgressSummary from '../../components/trip/TripProgressSummary';

const STATUS_LABELS = {
  SCHEDULED: 'Planifié',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const styles = StyleSheet.create({
  // Web only: the map image keeps its own 4:3 ratio instead of a fixed height (no cropping).
  mapContainer: { borderRadius: 8 },
});

function TripPublicTrackingScreen({ token }) {
  useEffect(() => {
    document.title = 'Suivi de trajet en direct — KCB';
  }, []);

  const [liveLocation, setLiveLocation] = useState(null);

  const { data, loading } = useQuery(TRIP_BY_SHARE_TOKEN, {
    variables: { token },
    skip: !token,
    // Pauses and the status change rarely and have no subscription: refresh gently.
    pollInterval: 30000,
  });

  useSubscription(DRIVER_LOCATION_BY_SHARE_TOKEN, {
    variables: { token },
    skip: !token || data?.tripByShareToken?.status !== 'IN_PROGRESS',
    onData: ({ data: subData }) => {
      const loc = subData?.data?.driverLocationByShareToken;
      if (loc) setLiveLocation(loc);
    },
  });

  const trip = data?.tripByShareToken;
  const lastLocation = liveLocation || trip?.driverLocations?.[0];
  // The server caches and shares this image between all viewers; the parameter only makes the
  // browser ask again when the driver position changed.
  const apiUrl = Constants.expoConfig?.extra?.EXPO_PUBLIC_API_URL ?? '';
  const mapUrl = `${apiUrl}/api/trip-map/${token}?t=${lastLocation?.timestamp ?? ''}`;

  // Coordinates are stored and served by the API, no geocoding from the visitor's browser.
  const departureCoords = useMemo(() => (trip?.departureLatitude != null
    ? { lat: trip.departureLatitude, lon: trip.departureLongitude } : null), [trip]);
  const arrivalCoords = useMemo(() => (trip?.arrivalLatitude != null
    ? { lat: trip.arrivalLatitude, lon: trip.arrivalLongitude } : null), [trip]);

  const routeData = useMemo(() => (trip?.routeGeometry
    ? { coordinates: decodePolyline(trip.routeGeometry) } : null), [trip?.routeGeometry]);

  const progress = useMemo(() => {
    if (!lastLocation || !departureCoords || !arrivalCoords) return 0;
    const total = calculateDistance(
      departureCoords.lat, departureCoords.lon,
      arrivalCoords.lat, arrivalCoords.lon,
    );
    if (total < 0.1) return 100;
    const remaining = calculateDistance(
      lastLocation.latitude, lastLocation.longitude,
      arrivalCoords.lat, arrivalCoords.lon,
    );
    return Math.max(0, Math.min(((total - remaining) / total) * 100, 100));
  }, [lastLocation, departureCoords, arrivalCoords]);

  if (!token) {
    return (
      <Center flex={1} p={6}>
        <Text color="red.500">Lien invalide.</Text>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center flex={1}>
        <Spinner size="lg" />
        <Text mt={4}>Chargement du trajet...</Text>
      </Center>
    );
  }

  if (!trip) {
    return (
      <Center flex={1} p={6}>
        <Card style={{ width: '100%', maxWidth: 400 }}>
          <Card.Content>
            <Text fontSize="lg" fontWeight="bold" color="red.500" textAlign="center">
              Ce lien a expiré
            </Text>
            <Text mt={2} color="gray.500" textAlign="center">
              Le lien de partage est invalide ou a expiré. Demandez un nouveau lien au conducteur.
            </Text>
          </Card.Content>
        </Card>
      </Center>
    );
  }

  const driverName = trip.guestDriverName || 'Conducteur';

  return (
    <ScrollView style={{ flex: 1, backgroundColor: '#f3f4f6' }}>
      <VStack p={4} space={4}>
        <Card>
          <Card.Title
            title="Suivi de trajet en direct — KCB"
            subtitle="Lien partagé · valide 12h"
          />
          <Card.Content>
            <VStack space={2}>
              <HStack justifyContent="space-between">
                <Text fontSize="sm" color="gray.600">Véhicule:</Text>
                <Text fontSize="sm" fontWeight="bold">
                  {trip.transportation?.name || '—'}
                </Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontSize="sm" color="gray.600">Départ:</Text>
                <Text fontSize="sm" fontWeight="bold" flex={1} textAlign="right" numberOfLines={2}>
                  {trip.departureLocation}
                </Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontSize="sm" color="gray.600">Arrivée:</Text>
                <Text fontSize="sm" fontWeight="bold" flex={1} textAlign="right" numberOfLines={2}>
                  {trip.arrivalLocation}
                </Text>
              </HStack>
              <HStack justifyContent="space-between">
                <Text fontSize="sm" color="gray.600">Heure de départ:</Text>
                <Text fontSize="sm">
                  {format(new Date(trip.departureDateTime), 'dd/MM/yyyy HH:mm')}
                </Text>
              </HStack>
              {trip.distanceInKm && (
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" color="gray.600">Distance:</Text>
                  <Text fontSize="sm">
                    {trip.distanceInKm.toFixed(1)}
                    {' '}
                    km
                  </Text>
                </HStack>
              )}
              <HStack justifyContent="space-between">
                <Text fontSize="sm" color="gray.600">Statut:</Text>
                <Text fontSize="sm" fontWeight="bold">
                  {STATUS_LABELS[trip.status] || trip.status}
                </Text>
              </HStack>
            </VStack>
          </Card.Content>
        </Card>

        {trip.status === 'COMPLETED' && (
          <Card>
            <Card.Content>
              <Center py={4}>
                <Text fontSize="lg" fontWeight="bold" color="green.600">Trajet terminé</Text>
                <Text mt={1} color="gray.500">Le conducteur est arrivé à destination.</Text>
              </Center>
            </Card.Content>
          </Card>
        )}

        {trip.status === 'IN_PROGRESS' && (
          <Card>
            <Card.Title
              title="Position du conducteur"
              subtitle="Mise à jour en temps réel"
            />
            <Card.Content>
              <VStack space={3}>
                {lastLocation ? (
                  <>
                    <Box borderRadius="md" overflow="hidden" style={styles.mapContainer}>
                      <TripMap
                        departureCoords={departureCoords}
                        arrivalCoords={arrivalCoords}
                        lastLocation={lastLocation}
                        routeData={routeData}
                        mapUrl={mapUrl}
                        trip={trip}
                      />
                    </Box>
                    <HStack space={4} justifyContent="center">
                      <HStack alignItems="center" space={1}>
                        <Box w={3} h={3} bg="green.500" borderRadius="full" />
                        <Text fontSize="xs" color="gray.600">Départ</Text>
                      </HStack>
                      <HStack alignItems="center" space={1}>
                        <Box w={3} h={3} bg="blue.500" borderRadius="full" />
                        <Text fontSize="xs" color="gray.600">Conducteur</Text>
                      </HStack>
                      <HStack alignItems="center" space={1}>
                        <Box w={3} h={3} bg="red.500" borderRadius="full" />
                        <Text fontSize="xs" color="gray.600">Arrivée</Text>
                      </HStack>
                    </HStack>
                    <TripProgressSummary
                      trip={trip}
                      routeGeometry={trip.routeGeometry}
                      lastLocation={lastLocation}
                      arrivalCoords={arrivalCoords}
                    />
                    <HStack justifyContent="space-between" alignItems="center">
                      <VStack>
                        <Text fontSize="xs" color="gray.500">Dernière mise à jour:</Text>
                        <Text fontSize="sm">
                          {format(new Date(lastLocation.timestamp), 'HH:mm:ss')}
                        </Text>
                      </VStack>
                    </HStack>
                  </>
                ) : (
                  <Center py={4}>
                    <Spinner size="sm" />
                    <Text mt={2} fontSize="sm" color="gray.500">
                      En attente de la position du conducteur...
                    </Text>
                  </Center>
                )}
              </VStack>
            </Card.Content>
          </Card>
        )}
      </VStack>
    </ScrollView>
  );
}

export default TripPublicTrackingScreen;
